import { Injectable } from "@angular/core";
import { Tools } from "@comunicare/comunicare-helper";
import { IAccount, IGlobalParameter, IIdentifier, IReference, USER_ROLE } from "@comunicare/comunicare-interfaces";
import { ISessionService } from "@comunicare/comunicare-interfaces-dashboard-services";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { environment } from "src/environments/environment";

export const enum PATH_URL {
  PRODUCTION = "",
  QA = "",
  DEV = ".eucip-dev.comunicare.link",
}

@Injectable({
  providedIn: "root",
})
export class SessionService implements ISessionService {
  private readonly tokenIndex = "access_token";
  private readonly idTokenIndex = "id_token";
  private readonly langIndex = "lang";
  private readonly serviceIndex = "service";
  private readonly organizationIndex = "organization";
  private readonly monitoringOrgIndex = "monitoringOrg";
  private readonly monitoringServiceIndex = "monitoringService";
  private readonly countryIndex = "country";

  private readonly patientPageFromIndex = "patientPageFrom";
  private readonly accountSubject = new BehaviorSubject<IAccount>(undefined);
  private readonly globalPrefSubject = new BehaviorSubject<IGlobalParameter>(undefined);
  private readonly tokenSubject = new BehaviorSubject<string>(undefined);
  private readonly COOKIE_ACCESS_TOKEN = "x-access-token";
  private patientListIndex = 0;

  public readonly refreshPatientUser = new Subject<void>();
  public readonly refreshPatientDataList = new Subject<void>();
  public readonly refreshQRDataList = new Subject<void>();
  public readonly refreshAlertDetailsDataList = new Subject<void>();
  public readonly refreshTeleconsultationsDataList = new Subject<void>();
  public readonly openCpModale = new Subject<void>();
  public readonly refreshGroupDataList = new Subject<void>();
  public readonly refreshPatientPage = new Subject<void>();
  public readonly refreshPatientWidgets = new Subject<void>();
  public readonly refreshDashboardPage = new Subject<void>();
  public readonly refreshDrugsData = new Subject<void>();
  public readonly refreshRewardDetailsDataList = new Subject<void>();
  public readonly refreshRedAlertWidget = new Subject<void>();
  public readonly refreshOrangeAlertWidget = new Subject<void>();
  public readonly refreshLastActivityWidget = new Subject<void>();
  public readonly refreshCp = new Subject<void>();
  public readonly refreshCommunicationsList = new Subject<void>();
  public readonly refreshRewardsList = new Subject<void>();
  public readonly refreshDrugSchemaList = new Subject<void>();
  public readonly refreshOrganizationsList = new Subject<void>();
  public readonly refreshVitalSignsTile = new Subject<void>();
  public readonly refreshServicesList = new Subject<void>();
  public readonly refreshCurrentOrganization = new Subject<void>();
  public readonly refreshCurrentService = new Subject<void>();
  public readonly refreshCurrentMonitService = new Subject<void>();
  public readonly refreshCurrentMonitOrg = new Subject<void>();
  public readonly refreshServerTraductions = new Subject<void>();
  public readonly refreshDeleteRequestList = new Subject<void>();
  public readonly refreshObservationsList = new Subject<void>();
  public readonly servicesSetupWatch = new Subject<void>();
  public readonly onGoingActionWatch = new BehaviorSubject<boolean>(false);

  public readonly DEFAULT_LANG = "fr";
  public readonly FRANCE = "FR";
  public readonly BELGIUM = "BE";
  public readonly allsOption = "all";
  public readonly allsReference: IReference = {
    reference: this.allsOption,
    display: this.allsOption,
  };
  public readonly drugSchemaEachRowsNumberOfDays = 31;
  public redirectUrl: string;
  private private_ANS_otherIDs: IIdentifier[] = [];
  public get ANS_otherIDs(): IIdentifier[] {
    return this.private_ANS_otherIDs;
  }
  public set ANS_otherIDs(value: IIdentifier[]) {
    this.private_ANS_otherIDs = value;
  }

  constructor() {
    this.tokenSubject.next(this.token);
  }

  public get pageIndex(): number {
    return this.patientListIndex;
  }

  public set pageIndex(v: number) {
    this.patientListIndex = v;
  }

  public needRefreshPatientUser(): void {
    this.refreshPatientUser.next();
  }

  public needRefreshPatientPage(): void {
    this.refreshPatientPage.next();
  }

  public needRefreshPatientWidgets(): void {
    this.refreshPatientWidgets.next();
  }

  public needRefreshDashboardPage(): void {
    this.refreshDashboardPage.next();
  }

  public needRefreshDrugsData(): void {
    this.refreshDrugsData.next();
  }

  public needRefreshPatientDataList(): void {
    this.refreshPatientDataList.next();
  }

  public needRefreshGroupDataList(): void {
    this.refreshGroupDataList.next();
  }

  public needRefreshCommunicationsList(): void {
    this.refreshCommunicationsList.next();
  }

  public needRefreshRewardsList(): void {
    this.refreshRewardsList.next();
  }

  public needRefreshDrugSchemaList(): void {
    this.refreshDrugSchemaList.next();
  }

  public needRefreshCp(): void {
    this.refreshCp.next();
  }

  public needRefresQRDataList(): void {
    this.refreshQRDataList.next();
  }

  public needOpenCpModale(): void {
    this.openCpModale.next();
  }

  public needRefreshAlertDetailsDataList(): void {
    this.refreshAlertDetailsDataList.next();
  }

  public needRefreshRewardDetailsDataList(): void {
    this.refreshRewardDetailsDataList.next();
  }

  public needRefreshTeleconsultationsDataList(): void {
    this.refreshTeleconsultationsDataList.next();
  }

  public needRefreshRedAlertWidget(): void {
    this.refreshRedAlertWidget.next();
  }

  public needRefreshOrangeAlertWidget(): void {
    this.refreshOrangeAlertWidget.next();
  }

  public needRefreshLastActivityWidget(): void {
    this.refreshLastActivityWidget.next();
  }

  private needRefreshServerTraductions(): void {
    this.refreshServerTraductions.next();
  }

  public needRefreshDeleteRequestList(): void {
    this.refreshDeleteRequestList.next();
  }
  public needRefreshObservationsList(): void {
    this.refreshObservationsList.next();
  }

  public needRefreshServicesList(): void {
    this.refreshServicesList.next();
  }

  public needRefreshVitalSignsTile(): void {
    this.refreshVitalSignsTile.next();
  }

  public needRefreshOrganizationsList(): void {
    this.refreshOrganizationsList.next();
  }

  public servicesHaveBeenSetup(): void {
    this.servicesSetupWatch.next();
  }

  public clear(): void {
    localStorage.removeItem(this.tokenIndex);
    localStorage.removeItem(this.idTokenIndex);
    localStorage.removeItem(this.serviceIndex);
    localStorage.removeItem(this.organizationIndex);
    localStorage.removeItem(this.monitoringOrgIndex);
    localStorage.removeItem(this.monitoringServiceIndex);
    this.ANS_otherIDs = [];
    this.accountSubject.next(undefined);
  }

  public set token(token: string) {
    localStorage.setItem(this.tokenIndex, token);
    this.tokenSubject.next(token);
  }

  public get token(): string {
    return localStorage.getItem(this.tokenIndex);
  }

  public set idToken(token: string) {
    localStorage.setItem(this.idTokenIndex, token);
  }

  public get idToken(): string {
    return localStorage.getItem(this.idTokenIndex);
  }
  public get pathByEnv(): string {
    return environment.production ? PATH_URL.PRODUCTION : environment.envName === "dev" ? PATH_URL.DEV : PATH_URL.QA;
  }

  public getObservableToken(): Observable<string> {
    return this.tokenSubject.asObservable();
  }

  public isLoggedIn(): boolean {
    return this.token !== null ? true : false;
  }

  public getRedirect(): string {
    const url = this.redirectUrl ? this.redirectUrl : "/dashboard";
    this.redirectUrl = undefined;
    return url;
  }

  public set userLang(lang: string) {
    localStorage.setItem(this.langIndex, lang);
    this.needRefreshServerTraductions();
    this.needRefreshCp();
  }

  public get userLang(): string {
    const lang = localStorage.getItem(this.langIndex);
    return lang ? lang : this.DEFAULT_LANG;
  }

  public set currentService(service: IReference) {
    const current = this.currentService;
    if (service?.reference === current?.reference) {
      return;
    }
    localStorage.setItem(this.serviceIndex, JSON.stringify(service));
    this.refreshCurrentService.next();
  }

  public get currentService(): IReference | null {
    const dep = localStorage.getItem(this.serviceIndex);
    return Tools.isDefined(dep) ? (JSON.parse(dep) as IReference) : null;
  }

  public set currentCountry(country: string) {
    const current = this.currentCountry;
    if (country === current) {
      return;
    }
    localStorage.setItem(this.countryIndex, country);
  }

  public get currentCountry(): string | null {
    const country = localStorage.getItem(this.countryIndex);
    return country ? country : null;
  }

  public set organization(orga: IReference) {
    const current = this.organization;
    if (orga?.reference === current?.reference) {
      return;
    }
    localStorage.setItem(this.organizationIndex, JSON.stringify(orga));
    this.refreshCurrentOrganization.next();
  }

  public get organization(): IReference | null {
    const orga = localStorage.getItem(this.organizationIndex);
    return orga && orga !== "undefined" ? (JSON.parse(orga) as IReference) : null;
  }

  public set currentMonitoringOrg(orga: IReference) {
    const current = this.currentMonitoringOrg;
    if (orga?.reference === current?.reference) {
      return;
    }
    localStorage.setItem(this.monitoringOrgIndex, JSON.stringify(orga));
    this.refreshCurrentMonitOrg.next();
  }

  public get currentMonitoringOrg(): IReference | null {
    const orga = localStorage.getItem(this.monitoringOrgIndex);
    return orga && orga !== "undefined" ? (JSON.parse(orga) as IReference) : null;
  }
  public set currentMonitoringService(service: IReference) {
    const current = this.currentMonitoringService;
    if (service?.reference === current?.reference) {
      return;
    }
    localStorage.setItem(this.monitoringServiceIndex, JSON.stringify(service));
    this.refreshCurrentMonitService.next();
  }

  public get currentMonitoringService(): IReference | null {
    const service = localStorage.getItem(this.monitoringServiceIndex);
    return Tools.isDefined(service) ? (JSON.parse(service) as IReference) : null;
  }

  public set account(account: IAccount) {
    this.accountSubject.next(account);
  }

  public get account(): IAccount {
    return this.accountSubject.getValue();
  }

  public getObservableAccount(): Observable<IAccount> {
    return this.accountSubject.asObservable();
  }

  public set globalPref(preference: IGlobalParameter) {
    this.globalPrefSubject.next(preference);
  }

  public get globalPref(): IGlobalParameter {
    return this.globalPrefSubject.getValue();
  }

  public getObservablePref(): Observable<IGlobalParameter> {
    return this.globalPrefSubject.asObservable();
  }

  public set patientPageFrom(url: string) {
    localStorage.setItem(this.patientPageFromIndex, url);
  }

  public get patientPageFrom(): string {
    return localStorage.getItem(this.patientPageFromIndex);
  }

  public isAdmin(): boolean {
    return this.account?.role.indexOf(USER_ROLE.ADMIN) >= 0;
  }

  public isOrgaAdmin(): boolean {
    return this.account?.role.indexOf(USER_ROLE.ORAGNIZATION_ADMIN) >= 0;
  }

  public isAllServices(isMonitoringUser: boolean): boolean {
    const current = isMonitoringUser ? this.currentMonitoringService : this.currentService;
    if (current && current.reference === this.allsOption) {
      return true;
    }
    return false;
  }

  /**
   * Toggle onGoingActionWatch value. Use to check if an ongoing action would be interrupted when changing service, organisation, monitoring service or monitoring organization.
   * E.g. When editing a knowledge Criteria.
   */
  public onGoingActionToggle(bool: boolean): void {
    if (bool) {
      this.onGoingActionWatch.next(true);
    } else {
      this.onGoingActionWatch.next(false);
    }
  }

  public onGoingAction(): boolean {
    return this.onGoingActionWatch.getValue();
  }
}
